import {
  RpcOutputStreamController,
  ServerStreamingCall,
  UnaryCall,
  type RpcInterceptor,
  type RpcMetadata,
  type RpcOptions,
} from '@protobuf-ts/runtime-rpc';

import { isUnauthenticatedError } from '$lib/api/errors';
import { isDebugLoggingEnabled } from '$lib/api/debug-logging';

import { isMissingTokenError, isNotLoggedInError } from './errors';

/**
 * Dependencies needed by `buildAuthInterceptor`. Factored out so the
 * interceptor can be unit-tested with mock auth-state without standing up
 * a full `AuthContext`.
 */
export interface AuthInterceptorDeps {
  /** True iff the user is currently `LoggedIn` (the interceptor is otherwise a no-op). */
  isLoggedIn: () => boolean;
  /** Returns the current Bearer token, refreshing proactively if it's expiring soon. */
  getAccessTokenForRequest: () => Promise<string>;
  /** Returns the cached Bearer token without refreshing. Used after a refresh+retry. */
  getAccessToken: () => string | undefined;
  /** Starts a token refresh (deduplicated across concurrent callers). */
  startRefresh: () => Promise<void>;
  /** Forces the user to the logged-out state and redirects to login. */
  logout: () => void;
}

/**
 * Attach no-op rejection handlers to the derived `headers`/`status`/`trailers`
 * promises on a wrapped call so that, if the underlying call rejects and the
 * consumer only awaits `response`/`responses`, the unobserved chains don't
 * surface as unhandled-promise-rejection noise (Sentry / browser warnings).
 * The promises are not replaced — awaiters still see the rejection.
 */
const shieldUnobservedRejections = (call: {
  headers: Promise<unknown>;
  status: Promise<unknown>;
  trailers: Promise<unknown>;
}): void => {
  call.headers.catch(() => undefined);
  call.status.catch(() => undefined);
  call.trailers.catch(() => undefined);
};

/** Build a fresh meta object seeded with the caller's metadata + the debug header. */
const seedMeta = (existing: RpcOptions['meta']): RpcMetadata => {
  const meta: RpcMetadata = { ...existing };
  if (isDebugLoggingEnabled()) {
    meta['x-viam-debug'] = '1';
  }
  return meta;
};

/**
 * Build the transport-level auth interceptor.
 *
 * - **Unary**: proactive token refresh + Bearer attach + refresh-and-retry-once
 *   on UNAUTHENTICATED.
 * - **Server streaming**: proactive pre-request refresh only — no mid-stream
 *   retry. Restarting an in-flight stream cleanly is out of scope and matches
 *   today's per-call streaming behavior.
 *
 * No-op while `deps.isLoggedIn()` returns false, so the pre-auth environment
 * bootstrap still goes through unchanged.
 *
 * Exported separately from the AuthContext factory so it can be unit-tested
 * with mock dependencies.
 */
export const buildAuthInterceptor = (
  deps: AuthInterceptorDeps
): RpcInterceptor => {
  const getInterceptorToken = async (): Promise<string> => {
    try {
      return await deps.getAccessTokenForRequest();
    } catch (error) {
      if (isMissingTokenError(error) || isNotLoggedInError(error)) {
        deps.logout();
      }
      throw error;
    }
  };

  return {
    interceptUnary: (next, method, input, options) => {
      // Pre-auth (env bootstrap, etc.) — pass through untouched.
      if (!deps.isLoggedIn()) {
        return next(method, input, options);
      }

      // Single shared meta object: passed by reference to `next()` and used as
      // the wrapped call's `requestHeaders`. Mutated in place once the token
      // is resolved so consumers (and tests) inspecting `wrapped.requestHeaders`
      // see the metadata actually sent on the wire.
      const meta = seedMeta(options.meta);

      const sendCall = (token: string) => {
        meta.Authorization = `Bearer ${token}`;
        return next(method, input, { ...options, meta });
      };

      // UnaryCall is PromiseLike, so returning one directly from an async
      // function would have the Promise machinery unwrap it into a
      // `FinishedUnaryCall` and lose the derived headers/response handles.
      // Box it.
      const innerCall = (async (): Promise<{ call: UnaryCall }> => {
        const token = await getInterceptorToken();
        const call = sendCall(token);

        try {
          await call.response;
          return { call };
        } catch (error) {
          if (!isUnauthenticatedError(error)) {
            throw error;
          }

          try {
            await deps.startRefresh();
          } catch (refreshError) {
            if (isNotLoggedInError(refreshError)) {
              deps.logout();
            }
            throw error;
          }

          const newToken = deps.getAccessToken();
          if (!newToken) {
            deps.logout();
            throw error;
          }

          return { call: sendCall(newToken) };
        }
      })();

      const wrapped = new UnaryCall(
        method,
        meta,
        input,
        innerCall.then(async ({ call }) => call.headers),
        innerCall.then(async ({ call }) => call.response),
        innerCall.then(async ({ call }) => call.status),
        innerCall.then(async ({ call }) => call.trailers)
      );
      shieldUnobservedRejections(wrapped);
      return wrapped;
    },

    interceptServerStreaming: (next, method, input, options) => {
      // Pre-auth (env bootstrap, etc.) — pass through untouched.
      if (!deps.isLoggedIn()) {
        return next(method, input, options);
      }

      const meta = seedMeta(options.meta);

      // Box the call (see comment in interceptUnary).
      const innerCall = (async (): Promise<{ call: ServerStreamingCall }> => {
        const token = await getInterceptorToken();
        meta.Authorization = `Bearer ${token}`;
        return { call: next(method, input, { ...options, meta }) };
      })();

      // Forward stream events through a controller we own so callers can
      // start consuming `responses` synchronously while we wait for the
      // token to arrive. No mid-stream retry on UNAUTHENTICATED — see
      // doc on buildAuthInterceptor.
      const responses = new RpcOutputStreamController();
      innerCall.then(
        ({ call }) => {
          call.responses.onNext((message, error, complete) => {
            responses.notifyNext(message, error, complete);
          });
        },
        (error: unknown) => {
          if (!responses.closed) {
            responses.notifyError(
              error instanceof Error ? error : new Error(String(error))
            );
          }
        }
      );

      const wrapped = new ServerStreamingCall(
        method,
        meta,
        input,
        innerCall.then(async ({ call }) => call.headers),
        responses,
        innerCall.then(async ({ call }) => call.status),
        innerCall.then(async ({ call }) => call.trailers)
      );
      shieldUnobservedRejections(wrapped);
      return wrapped;
    },
  };
};
